import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import Navigation from '@/components/Navigation';
import { Footer } from '@/components/Footer';
import { SEO } from '@/components/SEO';
import { ProductUploadForm } from '@/components/agent/ProductUploadForm';
import { BacklinksTable } from '@/components/agent/BacklinksTable';
import { BacklinkForm } from '@/components/agent/BacklinkForm';
import { ContainerCalculator } from '@/components/agent/ContainerCalculator';
import { BulkProductImport } from '@/components/agent/BulkProductImport';
import { SEOAutomation } from '@/components/agent/SEOAutomation';
import { EmailTemplateSystem } from '@/components/agent/EmailTemplateSystem';
import { BulkBacklinkImport } from '@/components/agent/BulkBacklinkImport';
import { BacklinkActivityLog } from '@/components/agent/BacklinkActivityLog';
import { BacklinkDashboard } from '@/components/agent/BacklinkDashboard';
import { ContentUploadSystem } from '@/components/agent/ContentUploadSystem';
import { ContentSyncDashboard } from '@/components/agent/ContentSyncDashboard';
import { KeywordTrackingDashboard } from '@/components/agent/KeywordTrackingDashboard';
import { AutomatedOutreachSender } from '@/components/agent/AutomatedOutreachSender';
import { OutreachAnalyticsDashboard } from '@/components/agent/OutreachAnalyticsDashboard';
import { ScheduledOutreachManager } from '@/components/agent/ScheduledOutreachManager';
import { WeeklySEOReport } from '@/components/agent/WeeklySEOReport';
import { CompetitorAnalysis } from '@/components/agent/CompetitorAnalysis';
import { SocialStudio } from '@/components/agent/SocialStudio';
import { toast } from 'sonner';
import { Plus, Link2, Download, Package, TrendingUp, CheckCircle, Clock, XCircle, Mail, Upload, Image as ImageIcon, BarChart3, Zap, Eye, FileText, Target, Wand2 } from 'lucide-react';

interface Product {
  id: string;
  name: string;
  model: string | null;
  unit_price: number;
  quantity: number;
  total_price: number | null;
  status: string;
  seo_score: number | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
}

interface Backlink {
  id: string;
  website_name: string;
  website_url: string;
  domain_authority: number | null;
  category: string;
  contact_email: string | null;
  contact_name: string | null;
  outreach_status: string;
  outreach_date: string | null;
  response_date: string | null;
  backlink_url: string | null;
  backlink_type: string | null;
  notes: string | null;
  created_at: string;
}

const AgentDashboard = () => {
  const { user, signOut, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [products, setProducts] = useState<Product[]>([]);
  const [backlinks, setBacklinks] = useState<Backlink[]>([]);
  const [loading, setLoading] = useState(true);
  const [isUploadOpen, setIsUploadOpen] = useState(false);
  const [isBacklinkOpen, setIsBacklinkOpen] = useState(false);

  useEffect(() => {
    if (!authLoading) {
      if (!user) {
        navigate('/agent-login');
      }
    }
  }, [user, authLoading, navigate]);

  useEffect(() => {
    if (user) {
      fetchProducts();
      fetchBacklinks();
    }
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('products')
      .select('*')
      .eq('agent_id', user?.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to fetch products');
    } else {
      setProducts(data || []);
    }
    setLoading(false);
  };

  const fetchBacklinks = async () => {
    const { data, error } = await supabase
      .from('backlinks')
      .select('*')
      .eq('agent_id', user?.id)
      .order('created_at', { ascending: false });

    if (error) {
      toast.error('Failed to fetch backlinks');
      console.error(error);
    } else {
      setBacklinks(data || []);
    }
  };

  const handleSignOut = async () => {
    await signOut();
    navigate('/');
  };

  const handleProductSubmitted = () => {
    setIsUploadOpen(false);
    fetchProducts();
  };

  const handleBacklinkSubmitted = () => {
    setIsBacklinkOpen(false);
    fetchBacklinks();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved':
        return 'default';
      case 'rejected':
        return 'destructive';
      default:
        return 'secondary';
    }
  };

  const getSEOColor = (score: number | null) => {
    if (!score) return 'text-muted-foreground';
    if (score >= 71) return 'text-green-600 dark:text-green-400';
    if (score >= 41) return 'text-orange-600 dark:text-orange-400';
    return 'text-red-600 dark:text-red-400';
  };

  const exportProductsToCSV = () => {
    if (products.length === 0) {
      toast.error('No products to export');
      return;
    }

    const headers = ['Date', 'Name', 'Model', 'Unit Price', 'Quantity', 'Total Price', 'SEO Score', 'Status'];
    const rows = products.map(p => [
      new Date(p.created_at).toLocaleDateString(),
      p.name,
      p.model || '-',
      p.unit_price.toFixed(2),
      p.quantity,
      p.total_price?.toFixed(2) || '-',
      p.seo_score || '-',
      p.status
    ]);

    const csvContent = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `products-export-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
    toast.success('Products exported successfully');
  };

  const productStats = {
    total: products.length,
    approved: products.filter(p => p.status === 'approved').length,
    pending: products.filter(p => p.status === 'pending').length,
    rejected: products.filter(p => p.status === 'rejected').length,
    avgSeoScore: products.length > 0 
      ? Math.round(products.reduce((sum, p) => sum + (p.seo_score || 0), 0) / products.length)
      : 0
  };

  if (authLoading || !user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </div>
    );
  }

  return (
    <>
      <SEO
        title="Agent Dashboard | Aureum VII Trading"
        description="Manage your product submissions"
        type="website"
        noindex
      />
      <div className="min-h-screen bg-background">
        <Navigation />
        
        <div className="container mx-auto px-4 py-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="text-3xl font-bold mb-2">Agent Dashboard</h1>
              <p className="text-muted-foreground">Signed in as: {user.email}</p>
            </div>
            <div className="flex gap-2">
              <Dialog open={isUploadOpen} onOpenChange={setIsUploadOpen}>
                <DialogTrigger asChild>
                  <Button>
                    <Plus className="mr-2 h-4 w-4" />
                    Upload Product
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>Upload New Product</DialogTitle>
                  </DialogHeader>
                  <ProductUploadForm onSuccess={handleProductSubmitted} />
                </DialogContent>
              </Dialog>
              <Button onClick={handleSignOut} variant="outline">
                Sign Out
              </Button>
            </div>
          </div>

          <Tabs defaultValue="overview" className="w-full">
            <TabsList className="grid w-full grid-cols-12 mb-6">
              <TabsTrigger value="overview">Overview</TabsTrigger>
              <TabsTrigger value="products">Products</TabsTrigger>
              <TabsTrigger value="backlinks">Backlinks</TabsTrigger>
              <TabsTrigger value="keywords">
                <BarChart3 className="w-4 h-4 mr-2" />
                Keywords
              </TabsTrigger>
              <TabsTrigger value="competitors">
                <Target className="w-4 h-4 mr-2" />
                Competitors
              </TabsTrigger>
              <TabsTrigger value="outreach">
                <Zap className="w-4 h-4 mr-2" />
                Outreach
              </TabsTrigger>
              <TabsTrigger value="reports">
                <FileText className="w-4 h-4 mr-2" />
                Reports
              </TabsTrigger>
              <TabsTrigger value="content-upload">
                <ImageIcon className="w-4 h-4 mr-2" />
                Content
              </TabsTrigger>
              <TabsTrigger value="bulk-import">
                <Upload className="w-4 h-4 mr-2" />
                Import
              </TabsTrigger>
              <TabsTrigger value="seo">SEO</TabsTrigger>
              <TabsTrigger value="email">
                <Mail className="w-4 h-4 mr-2" />
                Email
              </TabsTrigger>
              <TabsTrigger value="social">
                <Wand2 className="w-4 h-4 mr-2" />
                Social
              </TabsTrigger>
            </TabsList>

            <TabsContent value="overview">
              <div className="space-y-6">
                {/* Product Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Package className="h-5 w-5" />
                      Product Performance
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <div className="text-3xl font-bold">{productStats.total}</div>
                        <div className="text-sm text-muted-foreground mt-1">Total Products</div>
                      </div>
                      <div className="text-center p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="text-3xl font-bold text-green-600 dark:text-green-400 flex items-center justify-center gap-2">
                          <CheckCircle className="h-6 w-6" />
                          {productStats.approved}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Approved</div>
                      </div>
                      <div className="text-center p-4 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                        <div className="text-3xl font-bold text-orange-600 dark:text-orange-400 flex items-center justify-center gap-2">
                          <Clock className="h-6 w-6" />
                          {productStats.pending}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Pending Review</div>
                      </div>
                      <div className="text-center p-4 bg-red-500/10 border border-red-500/20 rounded-lg">
                        <div className="text-3xl font-bold text-red-600 dark:text-red-400 flex items-center justify-center gap-2">
                          <XCircle className="h-6 w-6" />
                          {productStats.rejected}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Rejected</div>
                      </div>
                      <div className="text-center p-4 bg-primary/10 border border-primary/20 rounded-lg">
                        <div className="text-3xl font-bold text-primary flex items-center justify-center gap-2">
                          <TrendingUp className="h-6 w-6" />
                          {productStats.avgSeoScore}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Avg SEO Score</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Backlink Stats */}
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <Link2 className="h-5 w-5" />
                      Backlink Outreach
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <div className="text-3xl font-bold">{backlinks.length}</div>
                        <div className="text-sm text-muted-foreground mt-1">Total Tracked</div>
                      </div>
                      <div className="text-center p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                        <div className="text-3xl font-bold text-green-600 dark:text-green-400">
                          {backlinks.filter(b => b.outreach_status === 'secured').length}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Secured</div>
                      </div>
                      <div className="text-center p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                        <div className="text-3xl font-bold text-blue-600 dark:text-blue-400">
                          {backlinks.filter(b => b.outreach_status === 'in_progress').length}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">In Progress</div>
                      </div>
                      <div className="text-center p-4 bg-muted rounded-lg">
                        <div className="text-3xl font-bold">
                          {backlinks.filter(b => b.outreach_status === 'not_contacted').length}
                        </div>
                        <div className="text-sm text-muted-foreground mt-1">Not Contacted</div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Quick Actions */}
                <Card>
                  <CardHeader>
                    <CardTitle>Quick Actions</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <Button 
                        onClick={() => setIsUploadOpen(true)} 
                        className="h-20 text-lg"
                        size="lg"
                      >
                        <Plus className="mr-2 h-6 w-6" />
                        Upload New Product
                      </Button>
                      <Button 
                        onClick={exportProductsToCSV} 
                        variant="outline"
                        className="h-20 text-lg"
                        size="lg"
                        disabled={products.length === 0}
                      >
                        <Download className="mr-2 h-6 w-6" />
                        Export Products to CSV
                      </Button>
                    </div>
                  </CardContent>
                </Card>

                {/* B2B Container Calculator */}
                <ContainerCalculator />
              </div>
            </TabsContent>

            <TabsContent value="products" className="space-y-6">
              <BulkProductImport />
              <ProductUploadForm onSuccess={fetchProducts} />
              
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>My Products</CardTitle>
                  <Button 
                    onClick={exportProductsToCSV} 
                    variant="outline"
                    size="sm"
                    disabled={products.length === 0}
                  >
                    <Download className="mr-2 h-4 w-4" />
                    Export CSV
                  </Button>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-center py-8 text-muted-foreground">Loading products...</p>
                  ) : products.length === 0 ? (
                    <p className="text-center py-8 text-muted-foreground">No products yet. Upload your first product!</p>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Date</TableHead>
                            <TableHead>Name</TableHead>
                            <TableHead>Model</TableHead>
                            <TableHead>Unit Price</TableHead>
                            <TableHead>Quantity</TableHead>
                            <TableHead>Total Price</TableHead>
                            <TableHead>SEO Score</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {products.map((product) => (
                            <TableRow key={product.id}>
                              <TableCell className="text-sm">
                                {new Date(product.created_at).toLocaleDateString()}
                              </TableCell>
                              <TableCell className="font-medium">{product.name}</TableCell>
                              <TableCell>{product.model || '-'}</TableCell>
                              <TableCell>€{product.unit_price.toFixed(2)}</TableCell>
                              <TableCell>{product.quantity}</TableCell>
                              <TableCell>
                                {product.total_price ? `€${product.total_price.toFixed(2)}` : '-'}
                              </TableCell>
                              <TableCell>
                                {product.seo_score ? (
                                  <span className={`font-semibold ${getSEOColor(product.seo_score)}`}>
                                    {product.seo_score}/100
                                  </span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </TableCell>
                              <TableCell>
                                <div className="space-y-1">
                                  <Badge variant={getStatusColor(product.status)}>
                                    {product.status}
                                  </Badge>
                                  {product.rejection_reason && (
                                    <p className="text-xs text-destructive">{product.rejection_reason}</p>
                                  )}
                                </div>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="backlinks">
              <Card>
                <CardHeader className="flex flex-row items-center justify-between">
                  <CardTitle>Backlink Tracking</CardTitle>
                  <Dialog open={isBacklinkOpen} onOpenChange={setIsBacklinkOpen}>
                    <DialogTrigger asChild>
                      <Button size="sm">
                        <Link2 className="mr-2 h-4 w-4" />
                        Add Backlink
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
                      <DialogHeader>
                        <DialogTitle>Add New Backlink</DialogTitle>
                      </DialogHeader>
                      <BacklinkForm onSuccess={handleBacklinkSubmitted} userId={user?.id || ''} />
                    </DialogContent>
                  </Dialog>
                </CardHeader>
                <CardContent>
                  {loading ? (
                    <p className="text-center py-8 text-muted-foreground">Loading backlinks...</p>
                  ) : (
                    <BacklinksTable backlinks={backlinks} onUpdate={fetchBacklinks} />
                  )}
                </CardContent>
              </Card>

              <Card className="mt-6">
                <CardHeader>
                  <CardTitle>Backlink Strategy Summary</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">{backlinks.length}</div>
                      <div className="text-sm text-muted-foreground">Total Tracked</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">
                        {backlinks.filter(b => b.outreach_status === 'secured').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Secured</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">
                        {backlinks.filter(b => b.outreach_status === 'in_progress').length}
                      </div>
                      <div className="text-sm text-muted-foreground">In Progress</div>
                    </div>
                    <div className="text-center p-4 bg-muted rounded-lg">
                      <div className="text-2xl font-bold">
                        {backlinks.filter(b => b.outreach_status === 'not_contacted').length}
                      </div>
                      <div className="text-sm text-muted-foreground">Not Contacted</div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="keywords">
              <KeywordTrackingDashboard userId={user?.id || ''} />
            </TabsContent>

            <TabsContent value="competitors">
              <CompetitorAnalysis />
            </TabsContent>

            <TabsContent value="outreach">
              <div className="space-y-6">
                <OutreachAnalyticsDashboard userId={user?.id || ''} />
                <ScheduledOutreachManager userId={user?.id || ''} />
                <AutomatedOutreachSender userId={user?.id || ''} />
              </div>
            </TabsContent>

            <TabsContent value="bulk-import">
              <div className="space-y-6">
                <BacklinkDashboard userId={user?.id || ''} />
                <BulkBacklinkImport 
                  userId={user?.id || ''} 
                  onImportComplete={fetchBacklinks} 
                />
                <BacklinkActivityLog userId={user?.id || ''} />
              </div>
            </TabsContent>

            <TabsContent value="reports">
              <WeeklySEOReport userId={user?.id || ''} />
            </TabsContent>

            <TabsContent value="seo">
              <SEOAutomation />
            </TabsContent>

            <TabsContent value="email">
              <EmailTemplateSystem />
            </TabsContent>

            <TabsContent value="social">
              <SocialStudio />
            </TabsContent>
          </Tabs>
        </div>

        <Footer />
      </div>
    </>
  );
};

export default AgentDashboard;